import {
  companyBrandingSchema,
  companyLogoPrepareUploadResponseSchema,
  type CompanyBrandingDTO,
  type CompanyLogoFinalizeUploadDTO,
  type CompanyLogoPrepareUploadDTO,
  type CompanyLogoPrepareUploadResponseDTO,
  type CompanyLogoRemoveDTO,
} from '@shared/schemas/company-branding';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';
import { CompanyUiError } from './company-errors';

function createUrl(path: string) {
  return new URL(path, apiBaseUrl);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as ({
    error?: { code?: string; details?: Record<string, unknown>; message?: string };
  } & T) | null;
  if (!response.ok || payload === null) {
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? response.statusText ?? 'La requête a échoué.',
      status: response.status,
    });
  }
  return payload;
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: Record<string, unknown>,
) {
  const response = await fetch(createUrl(path), {
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    method,
  });
  return parseResponse<T>(response);
}

export function getActiveCompanyBranding() {
  return request<{ data: CompanyBrandingDTO }>('GET', '/api/company/branding')
    .then((payload) => companyBrandingSchema.parse(payload.data));
}

export function getAdminCompanyBranding(companyId: string) {
  return request<{ data: CompanyBrandingDTO }>('GET', `/api/admin/companies/${companyId}/branding`)
    .then((payload) => companyBrandingSchema.parse(payload.data));
}

export function prepareActiveCompanyLogo(input: CompanyLogoPrepareUploadDTO) {
  return request<{ data: CompanyLogoPrepareUploadResponseDTO }>('POST', '/api/company/logo/prepare', input)
    .then((payload) => companyLogoPrepareUploadResponseSchema.parse(payload.data));
}

export function prepareAdminCompanyLogo(companyId: string, input: CompanyLogoPrepareUploadDTO) {
  return request<{ data: CompanyLogoPrepareUploadResponseDTO }>(
    'POST',
    `/api/admin/companies/${companyId}/logo/prepare`,
    input,
  ).then((payload) => companyLogoPrepareUploadResponseSchema.parse(payload.data));
}

export function finalizeActiveCompanyLogo(input: CompanyLogoFinalizeUploadDTO) {
  return request<{ data: CompanyBrandingDTO }>('POST', '/api/company/logo/finalize', input)
    .then((payload) => companyBrandingSchema.parse(payload.data));
}

export function finalizeAdminCompanyLogo(companyId: string, input: CompanyLogoFinalizeUploadDTO) {
  return request<{ data: CompanyBrandingDTO }>(
    'POST',
    `/api/admin/companies/${companyId}/logo/finalize`,
    input,
  ).then((payload) => companyBrandingSchema.parse(payload.data));
}

export function removeActiveCompanyLogo(input: CompanyLogoRemoveDTO) {
  return request<{ data: CompanyBrandingDTO }>('POST', '/api/company/logo/remove', input)
    .then((payload) => companyBrandingSchema.parse(payload.data));
}

export function removeAdminCompanyLogo(companyId: string, input: CompanyLogoRemoveDTO) {
  return request<{ data: CompanyBrandingDTO }>(
    'POST',
    `/api/admin/companies/${companyId}/logo/remove`,
    input,
  ).then((payload) => companyBrandingSchema.parse(payload.data));
}

async function putCompanyLogoToPresignedUrl(input: {
  file: File;
  headers: CompanyLogoPrepareUploadResponseDTO['headers'];
  method: CompanyLogoPrepareUploadResponseDTO['method'];
  uploadUrl: string;
}) {
  const uploadResponse = await fetch(input.uploadUrl, {
    body: input.file,
    headers: input.headers,
    method: input.method,
  });
  if (!uploadResponse.ok) {
    throw new CompanyUiError('Impossible d’envoyer le fichier logo vers le stockage.');
  }
}

export async function uploadActiveCompanyLogo(input: {
  file: File;
  variant: CompanyLogoPrepareUploadDTO['variant'];
}) {
  const prepared = await prepareActiveCompanyLogo({
    contentType: input.file.type as CompanyLogoPrepareUploadDTO['contentType'],
    filename: input.file.name,
    variant: input.variant,
  });
  await putCompanyLogoToPresignedUrl({
    file: input.file,
    headers: prepared.headers,
    method: prepared.method,
    uploadUrl: prepared.uploadUrl,
  });
  return finalizeActiveCompanyLogo({
    temporaryObjectKey: prepared.temporaryObjectKey,
    variant: input.variant,
  });
}

export async function uploadAdminCompanyLogo(input: {
  companyId: string;
  file: File;
  variant: CompanyLogoPrepareUploadDTO['variant'];
}) {
  const prepared = await prepareAdminCompanyLogo(input.companyId, {
    contentType: input.file.type as CompanyLogoPrepareUploadDTO['contentType'],
    filename: input.file.name,
    variant: input.variant,
  });
  await putCompanyLogoToPresignedUrl({
    file: input.file,
    headers: prepared.headers,
    method: prepared.method,
    uploadUrl: prepared.uploadUrl,
  });
  return finalizeAdminCompanyLogo(input.companyId, {
    temporaryObjectKey: prepared.temporaryObjectKey,
    variant: input.variant,
  });
}
